import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { getSupaClient } from "../../lib/supabase";
import { APIChannel } from "discord-api-types/v10";
import { DiscordCategory } from "@prisma/client";

const INVALID_TOKENS_ERROR = "User has no valid discord tokens";

/**
 * Custom hook for managing the admin box in settings section
 */
export default function useDotsAdmin() {
  const guildId = localStorage.getItem("guild-id") as string;
  const supabase = getSupaClient();

  const getGuildChannels = async function (guildId: string) {
    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/guilds/${guildId}/channels`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      }
    );

    const textResponse = await response.text();
    if (textResponse === INVALID_TOKENS_ERROR) {
      throw new Error(INVALID_TOKENS_ERROR);
    }

    return JSON.parse(textResponse);
  };

  const getGuildCategories = async function (guildId: string): Promise<any> {
    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/guilds/${guildId}/categories`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      }
    );

    const textResponse = await response.text();
    if (textResponse === INVALID_TOKENS_ERROR) {
      throw new Error(INVALID_TOKENS_ERROR);
    }

    const data = JSON.parse(textResponse);
    data.categories.map((category: DiscordCategory) => {
      // We get the date as a string from the API, so we need to convert it to a Date object
      category.createdAt = new Date(category.createdAt!);
      category.updatedAt = new Date(category.updatedAt!);
    });
    return data;
  };

  const getSubscriptionStatus = async (guildId: string): Promise<any> => {
    const { data, error } = await supabase
      .from("discord_guilds")
      .select("send_daily_digest")
      .eq("discord_id", guildId);

    if (data && data.length >= 0 && Array.isArray(data)) {
      return data;
    } else if (error) {
      throw new Error(error.message);
    } else {
      throw new Error(`Failed to fetch subscription status: ${guildId}`);
    }
  };

  const updateSubscriptionStatus = async (checked: boolean) => {
    const { error } = await supabase
      .from("discord_guilds")
      .update({ send_daily_digest: checked })
      .eq("discord_id", guildId);
    if (error) {
      throw new Error(error.message);
    } else {
      return "ok";
    }
  };

  const createDotsAdminChannel = async function () {
    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/guilds/${guildId}/create-dots-admin-channel`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      }
    );
    if (response.status === 201) {
      return "OK";
    }
    throw { msg: await response.text() };
  };

  const createCategory = async function ({
    name,
    type,
  }: {
    name: string;
    type: string;
  }) {
    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/guilds/${guildId}/categories`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ name, type }),
      }
    );

    if (response.ok) return response.ok;
    throw { msg: await response.text() };
  };

  const deleteChannelsByCategory = async function ({
    categoryId,
    guildId,
    startTime,
  }: {
    categoryId: string;
    guildId: string;
    startTime: number;
  }) {
    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/guilds/${guildId}/categories/${categoryId}/channels`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ startTime }),
      }
    );

    if (response.ok) return response.ok;
    throw { msg: await response.text() };
  };

  const updateUserDiscordTokens = async function ({
    code,
    guildId,
  }: {
    code: string;
    guildId: string;
  }) {
    const redirectUri = encodeURIComponent(
      `${process.env["REACT_APP_URI"]}/settings/`
    );

    const session = await supabase.auth.session();
    const response = await fetch(
      `${process.env["REACT_APP_API_URL"]}/users/${
        session!.user?.id
      }/discord-tokens?code=${code}&redirectUri=${redirectUri}&guildId=${guildId}`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      }
    );

    if (response.ok) return response.ok;
    throw { msg: await response.text() };
  };

  const guildChannels = useQuery<{ channels: APIChannel[] }>(
    ["channels", guildId],
    () => getGuildChannels(guildId),
    {
      enabled: false,
      retry: false,
    }
  );

  const guildCategories = useQuery<{ categories: DiscordCategory[] }>(
    ["categories", guildId],
    () => getGuildCategories(guildId),
    {
      enabled: false,
      retry: false,
    }
  );
  const dailyDigestSubscriptionStatus = useQuery<
    [{ send_daily_digest: boolean }]
  >(
    ["dailyDigestSubscriptionStatus", guildId],
    () => getSubscriptionStatus(guildId),
    {
      enabled: false,
    }
  );

  const dotsAdminChannelMutation = useMutation(createDotsAdminChannel);
  const createCategoryMutation = useMutation(createCategory);
  const deleteChannelsMutation = useMutation(deleteChannelsByCategory);
  const updateSubscriptionStatusMutation = useMutation(
    updateSubscriptionStatus
  );
  const updateUserDiscordTokensMutation = useMutation(updateUserDiscordTokens, {
    retry: false,
  });

  return {
    guildChannels,
    guildCategories,
    dailyDigestSubscriptionStatus,
    dotsAdminChannelMutation,
    createCategoryMutation,
    deleteChannelsMutation,
    updateSubscriptionStatusMutation,
    updateUserDiscordTokensMutation,
  };
}
